/**
=========================================================
* Trust Cargo - public website
=========================================================

* What a visitor to trust-cargo.co sees: the company site. The internal
* system is behind the Login button in the header, so the dashboard is no
* longer the front door.
*
* Arabic first with an English toggle - the customers are local, but the
* trade is international. The choice is remembered, and `dir` is set on the
* wrapper so the whole page mirrors without a second stylesheet.
*
* All copy and the contact details live in content.js.
*/

import { useEffect, useMemo, useState } from "react";

import SuiBox from "components/SuiBox";
import PageLayout from "examples/LayoutContainers/PageLayout";

import content, { COMPANY } from "./content";
import { C } from "./ui";
import Nav from "./Nav";
import Hero from "./Hero";
import { Stats, Services, Why, Process, News, Cta, Contact, Footer } from "./Sections";

const STORE_KEY = "tc_site_lang";

function Website() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem(STORE_KEY) || "ar";
    } catch (e) {
      return "ar";
    }
  });

  const t = content[lang] || content.ar;

  useEffect(() => {
    try {
      localStorage.setItem(STORE_KEY, lang);
    } catch (e) {
      // private browsing - the toggle still works for this visit
    }
  }, [lang]);

  /**
   * The dashboard sets dir on <body> from its own context. The public site
   * follows the visitor's language instead, and puts it back on the way out
   * so the internal pages are not left mirrored.
   */
  useEffect(() => {
    const previous = document.body.getAttribute("dir");
    document.body.setAttribute("dir", t.dir);
    document.documentElement.setAttribute("lang", lang);

    return () => {
      if (previous) document.body.setAttribute("dir", previous);
    };
  }, [t.dir, lang]);

  const sections = useMemo(
    () => [
      { id: "top", label: t.nav.home },
      { id: "services", label: t.nav.services },
      { id: "process", label: t.nav.process },
      { id: "news", label: t.nav.news },
      { id: "contact", label: t.nav.contact },
    ],
    [lang]
  );

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageLayout background="white">
      <SuiBox
        dir={t.dir}
        sx={{
          fontFamily: "'Tajawal', sans-serif",
          backgroundColor: C.ivory,
          // the header is fixed and transparent over the hero, so nothing
          // else may create a stacking context above it
          position: "relative",
        }}
      >
        <Nav t={t} lang={lang} onLang={setLang} sections={sections} />

        <Hero t={t} onCta={() => go("services")} />
        <Stats t={t} />
        <Services t={t} />
        <Why t={t} />
        <Process t={t} />
        <News t={t} />
        <Cta t={t} />
        <Contact t={t} lang={lang} />
        <Footer t={t} sections={sections} onGo={go} />

        {/* always-there WhatsApp button, the way people actually get in touch */}
        <SuiBox
          component="a"
          href={`https://wa.me/${COMPANY.whatsapp}`}
          target="_blank"
          rel="noreferrer"
          aria-label="WhatsApp"
          sx={{
            position: "fixed",
            insetInlineEnd: 22,
            bottom: 22,
            zIndex: 1300,
            width: 58,
            height: 58,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            textDecoration: "none",
            color: C.navy,
            fontSize: 26,
            background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
            boxShadow: "0 14px 34px rgba(201,162,39,0.45)",
            transition: "transform 200ms ease",
            "&:hover": { transform: "scale(1.06)" },
          }}
        >
          {/* an SVG so it reads as WhatsApp rather than a generic chat bubble */}
          <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.1c-.24.68-1.4 1.3-1.94 1.38-.5.07-1.13.1-1.82-.11-.42-.13-.96-.31-1.65-.61-2.9-1.25-4.8-4.17-4.94-4.37-.15-.2-1.19-1.58-1.19-3.02s.76-2.14 1.03-2.44c.27-.3.58-.37.78-.37h.56c.18 0 .42-.07.66.5.24.58.82 2 .89 2.15.07.15.12.32.02.51-.1.2-.15.32-.29.5-.15.17-.31.39-.44.52-.15.15-.3.31-.13.6.17.3.76 1.25 1.63 2.02 1.12.99 2.06 1.3 2.35 1.45.3.15.47.13.64-.08.17-.2.74-.86.94-1.16.2-.3.39-.25.66-.15.27.1 1.69.8 1.98.94.29.15.48.22.55.35.07.12.07.71-.17 1.39z" />
          </svg>
        </SuiBox>
      </SuiBox>
    </PageLayout>
  );
}

export default Website;
